// White-label theme bundle (CB-12). This directory is the ONLY place brand
// identity and design tokens may be hardcoded (INF-1 / gate exemption for theme/).
// A different tenant swaps this file; nothing else in the surface changes.

export const brand = {
  name: 'BookingDei',
  // The wordmark is set in two parts so the second half can take the accent
  // colour, as the design does. Splitting it here (rather than in a component)
  // keeps the brand string inside theme/, the only place INF-1 permits it.
  wordmark_head: 'Booking',
  wordmark_tail: 'Dei',
  tagline_en: 'Book appointments, venues & more across Bangladesh',
  tagline_bn: 'বাংলাদেশজুড়ে অ্যাপয়েন্টমেন্ট, ভেন্যু আর আরও অনেক কিছু বুক করুন',
} as const;

// The market this tenant serves. Copy in packages/i18n refers to it as {region}
// so no message carries a country literal (INF-1); a different tenant swaps this
// line and every string follows.
// Brand artwork. Paths live here because the filenames carry the brand name, and
// theme/ is the only place INF-1 permits a brand literal. A different tenant swaps
// these two files and the header and footer follow.
export const brandArt = {
  lockup_light: '/brand/bookingdei-ticket-lockup-en.png',
  lockup_dark: '/brand/bookingdei-ticket-lockup-en-reversed.png',
  // Browser-tab icon, injected at boot (main.tsx). Declared here rather than in
  // index.html because the filename is a brand literal (INF-1).
  icon: '/brand/bookingdei-ticket-icon.png',
} as const;

// The legal entity behind the deployment. Referenced from i18n copy as {company} so
// no message carries a company literal (INF-1).
export const company = {
  name: 'BetterDots Technologies',
  // Short form used where the design abbreviates the parent ("A BetterDots product").
  short: 'BetterDots',
} as const;

// The parent company's wordmark (About page "Who builds it"), light and reversed —
// filenames carry the company name, so they live here (INF-1).
export const companyArt = {
  wordmark_light: '/brand/betterdots-wordmark.svg',
  wordmark_dark: '/brand/betterdots-wordmark-reversed.svg',
} as const;

// Brand social handles (footer). Literals with the brand name, so they live in
// theme/ (INF-1); a different tenant swaps this block.
export const social = {
  facebook: 'https://facebook.com/bookingdei',
  instagram: 'https://instagram.com/bookingdei',
  youtube: 'https://youtube.com/@bookingdei',
  linkedin: 'https://www.linkedin.com/company/bookingdei',
  tiktok: 'https://www.tiktok.com/@bookingdei',
  x: 'https://x.com/bookingdei',
} as const;

export const region = {
  en: 'Bangladesh',
  bn: 'বাংলাদেশ',
} as const;

// The demo/default neighbourhood shown in the header location pill and the offers
// context line. A location literal, so it lives in theme/ (INF-1); i18n copy refers
// to it as {area}. A different tenant swaps this line and the copy follows.
export const demoArea = {
  en: 'Gulshan, Dhaka',
  bn: 'গুলশান, ঢাকা',
} as const;

// The deployment's IANA time zone — the api Worker's `TIMEZONE` var (INF-1: a market
// literal, so it lives in theme/). Date-only API values (`DateOnly`, e.g. the turf grid
// date of API-242) are calendar dates in this zone, so a visitor abroad still picks the
// venue's "today". The contract carries no zone (neither Business nor PublicSettings);
// a different tenant swaps this line alongside its Worker's TIMEZONE.
export const deploymentZone = 'Asia/Dhaka';

// Tenant support/office details. Brand + location literals live here (theme/) —
// the only place INF-1 permits them — so i18n copy stays tenant-blind. A different
// tenant swaps this block. `office` is multi-line (rendered with white-space).
export const support = {
  email: 'support@bookingdei.com',
  phone: '+880 9610-002211',
  // Support desk hours, per language (contact chip + hotline line, DC56).
  hours_en: '9 AM – 10 PM',
  hours_bn: 'সকাল ৯টা – রাত ১০টা',
} as const;

// Where the in-house editorial team writes from — the blog byline's second line
// when a post has no named author (DC56). A location literal, so it lives here.
export const editorialHome = {
  en: 'Dhaka, Bangladesh',
  bn: 'ঢাকা, বাংলাদেশ',
} as const;

// The brand monogram drawn in round avatars (blog byline, DC56 — design `t.ui.bd`).
export const brandMonogram = 'BD';

// Design tokens (CLAUDE.md design system). Mirrored as CSS custom properties in
// theme.css; kept here too so TS can reference status colours for chips.
export const color = {
  accent: '#0E7490',
  accentHover: '#0C6178',
  ink: '#101826',
  muted: '#5B6577',
  line: '#E4E7EC',
  surface: '#FFFFFF',
  canvas: '#F7F8FA',
} as const;

// Booking-status accent colours (CLAUDE.md): pending amber, payment_verification
// blue, confirmed green, cancelled red, plus the remaining machine states.
export const statusColor: Record<string, string> = {
  pending: '#B45309',
  payment_verification: '#1D4ED8',
  confirmed: '#15803D',
  in_progress: '#0E7490',
  completed: '#15803D',
  cancelled: '#B42318',
  no_show: '#B42318',
  rejected: '#B42318',
  expired: '#5B6577',
  refunded: '#6941C6',
};

export const radius = {
  pill: '999px',
  card: '18px',
  control: '12px',
} as const;

// The Listings pages' location-bound presentation (DR-81, DR-88). Location literals,
// so they live here: the search box's example terms per listing kind ({eg} in
// lr.web.cat.<kind>.search_ph), the four city labels the stylised results map
// prints (name, longitude, latitude, label offset), and the icon the City filter
// shows per city slug. A different tenant swaps these tables.
export const listingsSearchExamples = {
  hotel: { en: 'Gulshan, metro, beach', bn: 'গুলশান, মেট্রো, সৈকত' },
  restaurant: { en: 'buffet, Gulshan, Dhanmondi', bn: 'বুফে, গুলশান, ধানমন্ডি' },
  turf: { en: 'futsal, Uttara, Chattogram', bn: 'ফুটসাল, উত্তরা, চট্টগ্রাম' },
  generic: { en: 'Gulshan, metro, beach', bn: 'গুলশান, মেট্রো, সৈকত' },
} as const;

export const listingsMapLabels: readonly { en: string; bn: string; lng: number; lat: number; dx: number; dy: number }[] = [
  { en: 'Dhaka', bn: 'ঢাকা', lng: 90.4, lat: 23.78, dx: 10, dy: -14 },
  { en: 'Sylhet', bn: 'সিলেট', lng: 91.87, lat: 24.9, dx: -26, dy: -12 },
  { en: 'Bogura', bn: 'বগুড়া', lng: 89.37, lat: 24.85, dx: -38, dy: 4 },
  { en: 'Cox’s Bazar', bn: 'কক্সবাজার', lng: 91.98, lat: 21.43, dx: -62, dy: 6 },
];

export const listingsCityIcons: Readonly<Record<string, string>> = {
  dhaka: 'i-metro',
  chattogram: 'i-wave',
  'coxs-bazar': 'i-wave',
  khulna: 'i-tree',
  sylhet: 'i-tree',
  moulvibazar: 'i-tree',
  rangamati: 'i-land',
  bogura: 'i-land',
  cumilla: 'i-land',
};

// The "All categories" results view's Trust and Amenities filter chips (DR-82). The
// per-category chips are registry data (`filter_set`, `filterable`); the All view spans
// categories, so this deployment names the keys it shows there. Labels and icons still
// come from the registry vocabularies.
export const listingsAllFilters: { certificates: readonly string[]; amenities: readonly string[] } = {
  certificates: ['trade', 'fire', 'food', 'hotel'],
  amenities: ['wifi', 'parking', 'ac', 'family_zone', 'halal'],
};
